import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IFitoListItem } from '~/Components';
import { routeReplacer } from '~/Helpers/String.helper';
import { Route } from '~/Constants/Routes.constants';
import { useDeleteEventGroup } from '~/Screens/EventGroupList/hooks/useDeleteEventGroup';
import { useEndEventGroup } from '~/Screens/EventGroupList/hooks/useEndEventGroup';
import { useLanguage, useNavigation } from '~/Hooks';
import { selectEventGroupsList, selectNextEventsMap } from '~/store/eventGroups';
import { EventGroupsTab } from '~/Screens/EventGroupList/types';
import { isIntegratedEvent } from '~/Helpers/Utils.helpers';
import { EventGroup } from '~/Models/DTO/EventGroup.model';

export const useEventGroupActions = ({ selectedTab }: { selectedTab: EventGroupsTab }) => {
  const { t } = useLanguage();
  const { navigate } = useNavigation();
  const eventGroups = useSelector(selectEventGroupsList);
  const nextEventsMap = useSelector(selectNextEventsMap);

  const {
    endEventGroupOptions,
    onConfirmEndEventGroup,
    onOpenEndEventGroupModal,
    onCloseEndEventGroupModal,
  } = useEndEventGroup({
    shouldCleanNextEvent: true,
    shouldCleanEventGroupStore: selectedTab === EventGroupsTab.ACTIVE,
  });

  const {
    deleteEventGroupOptions,
    onConfirmDeleteEventGroup,
    onOpenDeleteEventGroupModal,
    onCloseDeleteEventGroupModal,
  } = useDeleteEventGroup();

  const actionType = useMemo(
    () => ({
      edit: {
        name: t('app.edit'),
        onClick: (item: IFitoListItem) => {
          navigate(
            routeReplacer(Route.EVENTGROUP_EDIT, {
              groupId: item.id,
              eventType: eventGroups.find(({ id }) => id === item.id)?.type || '',
            }),
          );
        },
      },
      end: {
        name: t('eventGroups.endEventGroup.title'),
        onClick: (item: IFitoListItem) => {
          const eventGroup = eventGroups.find(({ id }) => id === item.id);
          onOpenEndEventGroupModal(eventGroup);
        },
      },
      delete: {
        name: t('app.delete'),
        onClick: (item: IFitoListItem) => {
          const eventGroup = eventGroups.find(({ id }) => id === item.id);
          onOpenDeleteEventGroupModal(eventGroup);
        },
      },
    }),
    [navigate, eventGroups, onOpenDeleteEventGroupModal, onOpenEndEventGroupModal],
  );

  const onClick = (item: IFitoListItem) => {
    // prevent navigation to the edit page if the event group is from an integration
    const eventGroup = eventGroups.find(({ id }) => id === item.id);
    if (eventGroup && isIntegratedEvent(eventGroup)) {
      return;
    }

    navigate(
      routeReplacer(Route.EVENTGROUP_EDIT, {
        groupId: item.id,
        eventType: eventGroups.find(({ id }) => id === item.id)?.type || '',
      }),
    );
  };

  const getItemActions = useCallback(
    (item: IFitoListItem) => {
      const eventGroup = item as EventGroup;
      if (isIntegratedEvent(eventGroup)) {
        return [];
      }

      return [actionType.edit, nextEventsMap?.[item.id] ? actionType.end : actionType.delete];
    },
    [actionType, nextEventsMap],
  );

  return {
    getItemActions,
    onClick,
    endEventGroupOptions,
    deleteEventGroupOptions,
    onConfirmEndEventGroup,
    onCloseEndEventGroupModal,
    onConfirmDeleteEventGroup,
    onCloseDeleteEventGroupModal,
  };
};
